import { useEffect, useRef, useState } from "react";
import api, { apiError } from "../lib/api";
import { fmtDate, fmtMoney } from "../lib/format";
import { Card, SectionTitle } from "./shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Sparkles, FileDown, Loader2, MessageCircle, Undo2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, Legend } from "recharts";

export const SIT_STYLE = {
  "Al corriente": "bg-emerald-50 text-emerald-700",
  "Por vencer": "bg-amber-50 text-amber-700",
  "Vencido": "bg-red-50 text-red-700",
  "Sin plan": "bg-slate-100 text-slate-400",
};
const EST_FILA = {
  Pagado: "text-emerald-600", "Pagado parcial": "text-amber-600", Vencido: "text-red-600",
  "Por vencer": "text-amber-500", Pendiente: "text-slate-400",
};

export function SituacionChip({ cobranza }) {
  if (!cobranza) return null;
  const s = cobranza.situacion;
  return (
    <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold whitespace-nowrap ${SIT_STYLE[s] || SIT_STYLE["Sin plan"]}`}>
      {s === "Vencido" ? `Vencido ${cobranza.dias_atraso} días` : s}
    </span>
  );
}

// ---------- Plan de pagos ----------
const PLAN_FIELDS = [
  ["precio_venta", "Precio de venta ($)"], ["enganche_pct", "Enganche (%)"], ["enganche_monto", "Enganche ($, opcional)"],
  ["fecha_apartado", "Fecha de apartado", "date"], ["fecha_contrato", "Fecha de contrato", "date"],
  ["num_mensualidades", "No. de mensualidades"], ["monto_mensualidad", "Mensualidad ($, vacío = auto)"],
  ["dia_pago", "Día de pago (1-31)"], ["fecha_primer_pago", "Primer pago", "date"],
  ["contra_entrega_pct", "Contra entrega (%)"], ["fecha_entrega_estimada", "Entrega estimada", "date"],
];

export function PlanPagosModal({ unidad, open, onClose, onSaved, canWrite }) {
  const [f, setF] = useState({});
  const [cal, setCal] = useState(null);
  const [res, setRes] = useState(null);
  useEffect(() => {
    if (!open || !unidad) return;
    api.get(`/comercial/unidades/${unidad.id}/plan-pagos`).then((r) => {
      setF(r.data.plan || { precio_venta: unidad.precio_venta || unidad.precio_lista || "", enganche_pct: 20, num_mensualidades: 12, dia_pago: 5, moneda: "MXN" });
      setCal(r.data.calendario); setRes(r.data.resumen);
    });
  }, [open, unidad?.id]); // eslint-disable-line
  const guardar = async (e) => {
    e.preventDefault();
    try {
      const payload = {};
      PLAN_FIELDS.forEach(([k, , t]) => { payload[k] = t === "date" ? (f[k] || null) : Number(f[k]) || 0; });
      payload.num_mensualidades = parseInt(f.num_mensualidades) || 0;
      payload.dia_pago = parseInt(f.dia_pago) || 0;
      payload.moneda = f.moneda || "MXN"; payload.notas = f.notas || "";
      const r = await api.put(`/comercial/unidades/${unidad.id}/plan-pagos`, payload);
      setCal(r.data.calendario); setRes(r.data.resumen);
      toast.success("Plan de pagos guardado y calendario generado");
      onSaved();
    } catch (err) { toast.error(apiError(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="plan-pagos-modal">
        <DialogHeader><DialogTitle className="font-display text-[#002B49]">Plan de pagos — {unidad?.clave}</DialogTitle></DialogHeader>
        {canWrite && (
          <form onSubmit={guardar} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {PLAN_FIELDS.map(([k, label, t]) => (
              <div key={k}>
                <Label className="text-[11px]">{label}</Label>
                <Input data-testid={`pp-${k}`} type={t || "number"} step="any" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 h-8 text-sm" />
              </div>
            ))}
            <div className="flex items-end">
              <Button data-testid="pp-guardar" type="submit" size="sm" className="w-full bg-[#002B49] hover:bg-[#00395f]">Guardar y generar</Button>
            </div>
          </form>
        )}
        {res && res.situacion !== "Sin plan" && (
          <div className="flex flex-wrap gap-4 items-center text-sm border-t pt-3">
            <SituacionChip cobranza={res} />
            <span>Saldo: <b className="text-[#002B49]">{fmtMoney(res.saldo)}</b></span>
            {res.proximo_pago && <span>Próximo: <b>{fmtMoney(res.proximo_pago.monto)}</b> · {fmtDate(res.proximo_pago.fecha)}</span>}
            <Button data-testid="estado-cuenta-btn" variant="outline" size="sm" className="ml-auto gap-2 h-7 text-xs"
              onClick={async () => {
                try {
                  const r = await api.get(`/comercial/unidades/${unidad.id}/estado-cuenta-pdf`, { responseType: "blob" });
                  const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url; a.download = `estado_cuenta_${unidad.clave}.pdf`; a.click();
                  URL.revokeObjectURL(url);
                  toast.success("Estado de cuenta descargado, listo para enviar al cliente");
                } catch (e) { toast.error(apiError(e)); }
              }}>
              <FileDown size={12} /> Estado de cuenta PDF
            </Button>
          </div>
        )}
        {cal && (
          <div className="max-h-64 overflow-y-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b bg-slate-50 sticky top-0">
                <th className="px-3 py-2">#</th><th className="px-3 py-2">Concepto</th><th className="px-3 py-2">Vence</th>
                <th className="px-3 py-2 text-right">Programado</th><th className="px-3 py-2 text-right">Pagado</th><th className="px-3 py-2">Estatus</th>
              </tr></thead>
              <tbody>
                {cal.map((fila) => (
                  <tr key={fila.numero} className="border-b border-slate-50">
                    <td className="px-3 py-1.5">{fila.numero}</td>
                    <td className="px-3 py-1.5">{fila.concepto}</td>
                    <td className="px-3 py-1.5">{fmtDate(fila.fecha_vencimiento)}</td>
                    <td className="px-3 py-1.5 text-right">{fmtMoney(fila.monto_programado)}</td>
                    <td className="px-3 py-1.5 text-right">{fila.monto_pagado ? fmtMoney(fila.monto_pagado) : "—"}</td>
                    <td className={`px-3 py-1.5 font-bold ${EST_FILA[fila.estatus]}`}>{fila.estatus}{fila.dias_atraso > 0 ? ` (${fila.dias_atraso}d)` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {!cal && <p className="text-xs text-slate-400">Define el plan para generar el calendario de exhibiciones.</p>}
      </DialogContent>
    </Dialog>
  );
}

// ---------- Pestaña Cobranza ----------
export function TabCobranza({ pid, canWrite }) {
  const [d, setD] = useState(null);
  const [fSit, setFSit] = useState("todas");
  const [fCliente, setFCliente] = useState("");
  const [fMes, setFMes] = useState("");
  const [genId, setGenId] = useState(null);
  const load = () => api.get(`/properties/${pid}/cobranza`).then((r) => setD(r.data));
  useEffect(() => { load(); }, [pid]); // eslint-disable-line
  if (!d) return <p className="text-sm text-slate-400">Cargando cobranza...</p>;

  const filas = d.filas.filter((f) =>
    (fSit === "todas" || f.estatus === fSit) &&
    (!fCliente || (f.cliente + f.clave).toLowerCase().includes(fCliente.toLowerCase())) &&
    (!fMes || f.fecha_vencimiento.startsWith(fMes)));

  const excel = async () => {
    const r = await api.get(`/properties/${pid}/cobranza/excel`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url; a.download = `cobranza_${new Date().toISOString().slice(0, 10)}.xlsx`; a.click();
    URL.revokeObjectURL(url);
  };

  const recordatorio = async (f) => {
    setGenId(f.unidad_id);
    try {
      const r = await api.post(`/comercial/unidades/${f.unidad_id}/recordatorio`);
      await navigator.clipboard.writeText(r.data.mensaje).catch(() => {});
      toast.success("Recordatorio generado y copiado al portapapeles", { description: r.data.mensaje.slice(0, 140) + "..." });
    } catch (e) { toast.error(apiError(e)); }
    setGenId(null);
  };

  const t = d.totales;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
        {[["Programado", t.programado, "text-[#002B49]"], ["Cobrado", t.cobrado, "text-emerald-600"],
          ["Vencido", t.vencido, "text-red-600"], ["Por vencer 30 d", t.por_vencer_30, "text-amber-600"],
          ["Por vencer 60 d", t.por_vencer_60, "text-amber-500"], ["Por vencer 90 d", t.por_vencer_90, "text-slate-500"]].map(([l, v, c]) => (
          <Card key={l} className="p-3">
            <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">{l}</p>
            <p className={`font-display text-lg font-bold ${c}`}>{fmtMoney(v)}</p>
          </Card>
        ))}
      </div>

      <Card className="p-5" testId="grafica-flujo">
        <SectionTitle>Flujo de cobranza: esperado vs. real por mes</SectionTitle>
        {d.flujo.length === 0 ? <p className="text-sm text-slate-400">Define planes de pago para ver el flujo esperado.</p> : (
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={d.flujo}>
              <XAxis dataKey="mes" tick={{ fontSize: 10, fill: "#64748B" }} />
              <YAxis tick={{ fontSize: 10, fill: "#64748B" }} width={65} tickFormatter={(v) => `$${(v / 1e6).toFixed(1)}M`} />
              <RTooltip formatter={(v, n) => [fmtMoney(v), n === "programado" ? "Programado" : "Cobrado"]} contentStyle={{ borderRadius: 8, fontSize: 12 }} />
              <Legend formatter={(v) => v === "programado" ? "Programado" : "Cobrado"} />
              <Bar dataKey="programado" fill="#94A3B8" radius={[3, 3, 0, 0]} />
              <Bar dataKey="cobrado" fill="#00A3B5" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>

      <div className="flex flex-wrap gap-2 items-center">
        <Select value={fSit} onValueChange={setFSit}>
          <SelectTrigger data-testid="cobranza-filtro-situacion" className="w-44 h-9 bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todas">Todas las situaciones</SelectItem>
            {["Vencido", "Pagado parcial", "Por vencer", "Pendiente", "Pagado"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="Cliente o unidad..." value={fCliente} onChange={(e) => setFCliente(e.target.value)} className="w-48 h-9 bg-white" />
        <Input type="month" value={fMes} onChange={(e) => setFMes(e.target.value)} className="w-40 h-9 bg-white" />
        <Button data-testid="cobranza-excel" variant="outline" size="sm" onClick={excel} className="ml-auto gap-2"><FileDown size={13} /> Exportar Excel</Button>
      </div>

      <Card className="overflow-x-auto" testId="tabla-cobranza">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-400 border-b">
            <th className="px-4 py-3">Unidad</th><th className="px-3 py-3">Cliente</th><th className="px-3 py-3">Concepto</th>
            <th className="px-3 py-3">Vence</th><th className="px-3 py-3 text-right">Programado</th>
            <th className="px-3 py-3 text-right">Pagado</th><th className="px-3 py-3">Estatus</th><th className="px-3 py-3"></th>
          </tr></thead>
          <tbody>
            {filas.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-400 text-sm">Sin pagos programados. Define planes de pago en el Inventario.</td></tr>}
            {filas.slice(0, 200).map((f, i) => (
              <tr key={`${f.unidad_id}-${f.numero}-${i}`} className="border-b border-slate-50">
                <td className="px-4 py-2 font-semibold text-[#002B49]">{f.clave}</td>
                <td className="px-3 py-2 text-xs">{f.cliente || "—"}</td>
                <td className="px-3 py-2 text-xs">{f.concepto} #{f.numero}</td>
                <td className="px-3 py-2 text-xs">{fmtDate(f.fecha_vencimiento)}</td>
                <td className="px-3 py-2 text-right">{fmtMoney(f.monto_programado)}</td>
                <td className="px-3 py-2 text-right text-emerald-700">{f.monto_pagado ? fmtMoney(f.monto_pagado) : "—"}</td>
                <td className={`px-3 py-2 text-xs font-bold ${EST_FILA[f.estatus]}`}>{f.estatus}{f.dias_atraso > 0 ? ` · ${f.dias_atraso}d` : ""}</td>
                <td className="px-3 py-2">
                  {canWrite && (f.estatus === "Vencido" || f.estatus === "Pagado parcial" || f.estatus === "Por vencer") && (
                    <button data-testid={`recordatorio-${f.clave}`} title="Generar recordatorio con IA (copia al portapapeles)"
                      onClick={() => recordatorio(f)} disabled={genId === f.unidad_id}
                      className="p-1.5 text-slate-400 hover:text-[#00A3B5] disabled:opacity-40">
                      {genId === f.unidad_id ? <Loader2 size={14} className="animate-spin" /> : <MessageCircle size={14} />}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ---------- Importador con IA (asistente 4 pasos) ----------
const CAMPOS_IMPORT = ["clave", "tipo", "m2", "precio_lista", "estatus", "cliente_nombre", "cliente_contacto",
  "precio_venta", "fecha_apartado", "fecha_venta", "enganche_monto", "enganche_pct", "num_mensualidades",
  "monto_mensualidad", "fecha_primer_pago", "abono", "fecha_pago", "saldo", "fecha_entrega", "ignorar"];

export function ImportadorIA({ pid, onDone }) {
  const [open, setOpen] = useState(false);
  const [s, setS] = useState(null); // sesión
  const [acciones, setAcciones] = useState({});
  const [resumen, setResumen] = useState(null);
  const inputRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => () => timer.current && clearInterval(timer.current), []);

  const subir = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/properties/${pid}/comercial/importar`, fd);
      setS({ id: r.data.import_id, estado: "analizando", nombre_original: file.name });
      setResumen(null); setAcciones({});
      timer.current = setInterval(async () => {
        const rr = await api.get(`/comercial/importar/${r.data.import_id}`);
        if (rr.data.estado !== "analizando") {
          clearInterval(timer.current);
          setS(rr.data);
          const acc = {};
          (rr.data.match || []).forEach((m) => { acc[m.index] = m.accion === "conflicto" ? "ignorar" : m.accion; });
          setAcciones(acc);
        }
      }, 2500);
    } catch (e) { toast.error(apiError(e)); }
  };

  const remapear = async (indice, campo) => {
    const mapeo = (s.mapeo || []).map((m) => (m.indice === indice ? { ...m, campo } : m));
    try {
      const r = await api.post(`/comercial/importar/${s.id}/mapeo`, { mapeo });
      setS({ ...s, mapeo, filas: r.data.filas, match: r.data.match });
      const acc = {};
      r.data.match.forEach((m) => { acc[m.index] = m.accion === "conflicto" ? "ignorar" : m.accion; });
      setAcciones(acc);
    } catch (e) { toast.error(apiError(e)); }
  };

  const aplicar = async () => {
    try {
      const r = await api.post(`/comercial/importar/${s.id}/aplicar`, { acciones });
      setResumen(r.data.resumen);
      toast.success(`Importación aplicada: ${r.data.resumen.nuevas} nuevas, ${r.data.resumen.actualizadas} actualizadas`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  const deshacer = async () => {
    try {
      await api.post(`/comercial/importar/${s.id}/deshacer`);
      toast.success("Importación deshecha; los datos volvieron a su estado anterior");
      setResumen(null); setS(null); onDone();
    } catch (e) { toast.error(apiError(e)); }
  };

  const match = s?.match || [];
  const nConf = match.filter((m) => m.accion === "conflicto").length;

  return (
    <>
      <Button data-testid="importar-ia-button" variant="outline" onClick={() => { setOpen(true); }} className="gap-2">
        <Sparkles size={14} className="text-[#00A3B5]" /> Importar con IA
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (!v) { setOpen(false); } }}>
        <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="importador-modal">
          <DialogHeader><DialogTitle className="font-display text-[#002B49] flex items-center gap-2"><Sparkles size={16} className="text-[#00A3B5]" /> Importar inventario y ventas con IA</DialogTitle></DialogHeader>

          {!s && (
            <div onClick={() => inputRef.current?.click()} data-testid="importador-dropzone"
              className="rounded-xl border-2 border-dashed border-slate-300 hover:border-[#00A3B5]/60 p-8 text-center cursor-pointer">
              <input ref={inputRef} type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden"
                onChange={(e) => { if (e.target.files?.[0]) subir(e.target.files[0]); e.target.value = ""; }} />
              <p className="text-sm font-semibold text-[#002B49]">Sube tu Excel, CSV o PDF comercial</p>
              <p className="text-[11px] text-slate-400 mt-1">Listas de precios, reportes de ventas con clientes y abonos, estados de cuenta o contratos.<br />La IA detecta la estructura, tú revisas el mapeo y cada fila antes de aplicar. Reversible durante 24 h.</p>
            </div>
          )}

          {s?.estado === "analizando" && (
            <p className="text-sm text-[#00A3B5] flex items-center gap-2 py-6 justify-center"><Loader2 size={15} className="animate-spin" /> Analizando “{s.nombre_original}”: detectando estructura y mapeando columnas...</p>
          )}
          {s?.estado === "error" && <p className="text-sm text-red-500">{s.error}</p>}

          {s?.estado === "listo" && !resumen && (
            <div className="space-y-4">
              <div className={`rounded-lg px-3 py-2 text-xs ${s.veredicto === "PERTENECE" ? "bg-emerald-50 text-emerald-800" : s.veredicto === "DUDA" ? "bg-amber-50 text-amber-800" : "bg-red-50 text-red-700"}`} data-testid="importador-veredicto">
                <b>{s.tipo_archivo === "inventario" ? "Lista de precios / inventario" : s.tipo_archivo === "ventas_clientes" ? "Reporte de ventas con clientes" : s.tipo_archivo === "plan_pagos_cliente" ? "Plan de pagos / estado de cuenta" : s.tipo_archivo === "contrato" ? "Contrato de compraventa" : "Archivo"}</b> · {s.veredicto} · {s.razon}
              </div>

              {s.headers?.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">Mapeo de columnas (editable)</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {(s.mapeo || []).map((m) => (
                      <div key={m.indice} className="border rounded-lg p-2">
                        <p className="text-[11px] font-semibold text-[#002B49] truncate">{s.headers[m.indice] || `Col ${m.indice + 1}`}</p>
                        <Select value={m.campo || "ignorar"} onValueChange={(v) => remapear(m.indice, v)}>
                          <SelectTrigger data-testid={`mapeo-${m.indice}`} className="h-7 text-[11px] mt-1"><SelectValue /></SelectTrigger>
                          <SelectContent>{CAMPOS_IMPORT.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2">
                  Previsualización · {match.length} filas {nConf > 0 && <span className="text-red-500">· {nConf} conflictos</span>}
                </p>
                <div className="max-h-72 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead><tr className="text-left text-[10px] uppercase text-slate-400 border-b bg-slate-50 sticky top-0">
                      <th className="px-3 py-2">Clave</th><th className="px-3 py-2">Cliente</th><th className="px-3 py-2 text-right">Precio</th>
                      <th className="px-3 py-2">Detalles</th><th className="px-3 py-2">Acción</th>
                    </tr></thead>
                    <tbody>
                      {match.map((m) => (
                        <tr key={m.index} className={`border-b border-slate-50 ${m.accion === "conflicto" ? "bg-red-50/50" : ""}`} data-testid={`fila-import-${m.index}`}>
                          <td className="px-3 py-1.5 font-semibold">{m.fila.clave}</td>
                          <td className="px-3 py-1.5">{m.fila.cliente_nombre || "—"}</td>
                          <td className="px-3 py-1.5 text-right">{m.fila.precio_venta ? fmtMoney(m.fila.precio_venta) : m.fila.precio_lista ? fmtMoney(m.fila.precio_lista) : "—"}</td>
                          <td className="px-3 py-1.5 text-[11px] text-slate-500">
                            {m.accion === "conflicto" ? m.conflictos.map((c) => `${c.campo}: ${c.actual} → ${c.importado}`).join(" · ")
                              : [m.fila.estatus, m.fila.abono ? `abono ${fmtMoney(m.fila.abono)}` : null, m.fila.num_mensualidades ? `${m.fila.num_mensualidades} mens.` : null].filter(Boolean).join(" · ") || "—"}
                          </td>
                          <td className="px-3 py-1.5">
                            <Select value={acciones[m.index] || "ignorar"} onValueChange={(v) => setAcciones({ ...acciones, [m.index]: v })}>
                              <SelectTrigger data-testid={`accion-${m.index}`} className="h-6 text-[11px] w-32"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="nueva">Nueva unidad</SelectItem>
                                {m.unidad_id && <SelectItem value="actualizar">Actualizar {m.clave_existente}</SelectItem>}
                                <SelectItem value="ignorar">Ignorar</SelectItem>
                              </SelectContent>
                            </Select>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="flex items-center justify-between">
                <p className="text-[11px] text-slate-400">
                  {Object.values(acciones).filter((a) => a === "nueva").length} nuevas · {Object.values(acciones).filter((a) => a === "actualizar").length} a actualizar · {Object.values(acciones).filter((a) => a === "ignorar").length} ignoradas
                </p>
                <Button data-testid="importador-aplicar" onClick={aplicar} className="bg-[#002B49] hover:bg-[#00395f]">Aplicar importación</Button>
              </div>
            </div>
          )}

          {resumen && (
            <div className="text-center py-6 space-y-3" data-testid="importador-resumen">
              <p className="font-display text-xl font-bold text-[#002B49]">Importación aplicada</p>
              <p className="text-sm text-slate-500">{resumen.nuevas} unidades nuevas · {resumen.actualizadas} actualizadas · {resumen.ignoradas} ignoradas</p>
              <p className="text-[11px] text-slate-400">Quedó registrada en la bitácora. Puedes revertirla durante 24 horas.</p>
              <div className="flex gap-2 justify-center">
                <Button data-testid="importador-deshacer" variant="outline" size="sm" onClick={deshacer} className="gap-2"><Undo2 size={13} /> Deshacer importación</Button>
                <Button size="sm" onClick={() => { setOpen(false); setS(null); setResumen(null); }} className="bg-[#002B49]">Cerrar</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
